/* eslint-disable react/prop-types */
// /CoreWrapper/cores/coreOrbit.js
import React, { useLayoutEffect, useRef, useState } from 'react'

export function coreOrbit(BaseComponent) {
	const WithOrbit = ({
		orbit = false,
		orbitElement,
		orbitSize = 10,
		orbitSpeed = '4s',
		orbitDirection = 'clockwise', // 'clockwise' | 'counterclockwise'
		orbitInset = 0, // distance from wrapper edge to dot center
		orbitRotateWithPath = false,
		orbitDebug = false,
		className = '',
		style = {},
		fullWidth = false,
		...rest
	}) => {
		if (!orbit) {
			return (
				<BaseComponent
					fullWidth={fullWidth}
					className={className}
					style={style}
					{...rest}
				/>
			)
		}

		const wrapRef = useRef(null)
		const [path, setPath] = useState('')
		const [debugBox, setDebugBox] = useState(null)

		useLayoutEffect(() => {
			const el = wrapRef.current
			if (!el) return

			const computePath = () => {
				const rect = el.getBoundingClientRect()
				const w = rect.width
				const h = rect.height

				if (!w || !h) {
					setPath('')
					setDebugBox(null)
					return
				}

				const baseInset =
					(typeof orbitInset === 'number' ? orbitInset : 0) +
					orbitSize / 2

				const left = baseInset
				const top = baseInset
				const right = w - baseInset
				const bottom = h - baseInset

				if (right <= left || bottom <= top) {
					setPath('')
					setDebugBox(null)
					return
				}

				const d = [
					`M ${left} ${top}`,
					`H ${right}`,
					`V ${bottom}`,
					`H ${left}`,
					`V ${top}`,
					'Z'
				].join(' ')

				setPath(d)

				if (orbitDebug) {
					setDebugBox({
						left,
						top,
						width: right - left,
						height: bottom - top
					})
				} else {
					setDebugBox(null)
				}
			}

			// initial
			computePath()

			// observe size changes for correct layout after refresh / flex / etc
			if (typeof ResizeObserver !== 'undefined') {
				const ro = new ResizeObserver(() => computePath())
				ro.observe(el)
				return () => ro.disconnect()
			}

			// fallback: recompute on resize
			const onResize = () => computePath()
			window.addEventListener('resize', onResize)
			return () => window.removeEventListener('resize', onResize)
		}, [orbitInset, orbitSize, orbitDebug])

		const dir = orbitDirection === 'counterclockwise' ? 'reverse' : 'normal'

		const wrapClass = ['wbk-orbit-wrap', fullWidth && 'wbk-wrapper--full']
			.filter(Boolean)
			.join(' ')

		const wrapStyle = fullWidth ? { width: '100%' } : undefined

		const baseClass = className
		const baseStyle = style

		return (
			<div ref={wrapRef} className={wrapClass} style={wrapStyle}>
				<BaseComponent
					fullWidth={fullWidth}
					className={baseClass}
					style={baseStyle}
					{...rest}
				/>

				{debugBox && (
					<div
						className="wbk-orbit-debug"
						style={{
							left: `${debugBox.left}px`,
							top: `${debugBox.top}px`,
							width: `${debugBox.width}px`,
							height: `${debugBox.height}px`
						}}
					/>
				)}

				{path && (
					<div
						className="wbk-orbit-item"
						style={{
							width: `${orbitSize}px`,
							height: `${orbitSize}px`,
							offsetPath: `path("${path}")`,
							animationDuration: orbitSpeed,
							animationDirection: dir,
							offsetRotate: orbitRotateWithPath ? 'auto' : '0deg'
						}}
					>
						{orbitElement || <div className="wbk-orbit-dot" />}
					</div>
				)}
			</div>
		)
	}

	return WithOrbit
}
